import type { Mixer } from './mixer';
import { bellPartials, midiToHz, noiseBurst, ringTick, tone } from './synth';

/**
 * Synthesised period instruments shared by the radio's songs and the stingers. All schedule at audio
 * time `t` into `dest`; `ess` marks them essential (radio voices ignore the one-shot voice cap).
 * Keys: Rhodes-style electric piano (FM tine + bark), Hammond-style organ (drawbar PeriodicWaves with
 * percussion and key click), upright piano; guitars (twang / palm-muted chop); strings and flute with
 * delayed vibrato; brass; vibes; melodica; four basses; a drum kit with brushes and sticks, tambourine,
 * shaker, congas, ride and crash.
 */

export type DrumKind = 'kick' | 'kickSoft' | 'snare' | 'brush' | 'hat' | 'hatOpen' | 'rim' | 'ride' | 'crash' | 'tomLo' | 'tomHi' | 'tamb' | 'shaker' | 'conga';

/** Rhodes-style electric piano: 2-operator FM tine (1:1) with a bright "bark" partial on hard hits. */
export function epiano(mx: Mixer, dest: AudioNode, t: number, midi: number, dur: number, vel: number, ess = false): void {
  const ctx = mx.ctx;
  if (!ctx || !mx.canSpawn(ess)) return;
  const f = midiToHz(midi);
  const carrier = ctx.createOscillator();
  const mod = ctx.createOscillator();
  const modGain = ctx.createGain();
  const amp = ctx.createGain();
  carrier.frequency.value = f;
  mod.frequency.value = f;
  const idx = f * (0.6 + vel * 1.5);
  modGain.gain.setValueAtTime(idx, t);
  modGain.gain.exponentialRampToValueAtTime(Math.max(1, idx * 0.06), t + Math.min(0.9, dur + 0.2));
  mod.connect(modGain).connect(carrier.frequency);
  const end = t + dur + 0.45;
  amp.gain.setValueAtTime(0.0001, t);
  amp.gain.linearRampToValueAtTime(0.22 * vel, t + 0.004);
  amp.gain.exponentialRampToValueAtTime(0.09 * vel, t + Math.min(dur, 0.45));
  amp.gain.exponentialRampToValueAtTime(0.0001, end);
  carrier.connect(amp).connect(dest);
  mx.voiceCount += 2;
  carrier.onended = () => {
    mx.voiceCount -= 2;
    for (const n of [carrier, mod, modGain, amp]) n.disconnect();
  };
  carrier.start(t);
  mod.start(t);
  carrier.stop(end + 0.03);
  mod.stop(end + 0.03);
  // The bark: a short partial two octaves up, only on firm touches.
  if (vel > 0.6) tone(mx, { type: 'sine', f0: f * 4, t, dur: 0.07, gain: 0.05 * (vel - 0.6), attack: 0.002, dest, essential: ess });
}

export const rhodes = epiano;

/** Hammond registrations as drawbar levels for harmonics of the 16' fundamental (f/2): [1, 2, 4, 6, 8, 10, 12, 16]. */
const REGISTRATIONS: Record<string, readonly number[]> = {
  // 88 8000 000: the jazz / soul comp sound.
  comp: [1, 1, 1, 0, 0, 0, 0, 0],
  // 88 8800 000 + a little 2': the solo registration.
  solo: [1, 1, 1, 0.8, 0.45, 0, 0, 0],
  // 80 8000 000: hollow, for pads under a chorus.
  pad: [1, 0, 1, 0, 0, 0, 0, 0],
  // 00 8808 000: reggae bubble, nasal.
  bubble: [0, 0, 1, 1, 0, 1, 0, 0],
};

const HARMONICS = [1, 2, 4, 6, 8, 10, 12, 16];

function organWave(mx: Mixer, reg: string): PeriodicWave {
  return mx.periodicWave(`organ:${reg}`, () => {
    const levels = REGISTRATIONS[reg] as readonly number[];
    const real = new Float32Array(17);
    const imag = new Float32Array(17);
    let norm = 0;
    for (let i = 0; i < HARMONICS.length; i++) {
      const h = HARMONICS[i] as number;
      const l = levels[i] as number;
      imag[h] = l;
      norm += l * l;
    }
    const s = 1 / Math.sqrt(Math.max(1e-6, norm));
    for (let i = 0; i < imag.length; i++) imag[i] = (imag[i] as number) * s;
    return { real, imag };
  });
}

export type OrganReg = keyof typeof REGISTRATIONS;

/**
 * Hammond-style organ: one band-limited PeriodicWave per registration (the 16' fundamental sits an
 * octave under the written note), a decaying percussion partial, a key click, and a slow scanner
 * vibrato for the solo registration.
 */
export function hammond(mx: Mixer, dest: AudioNode, t: number, midi: number, dur: number, vel: number, ess = false, reg: OrganReg = 'comp'): void {
  const f = midiToHz(midi);
  const d = Math.max(0.06, dur);
  const wave = organWave(mx, reg);
  const solo = reg === 'solo';
  tone(mx, { wave, f0: f / 2, t, dur: d, gain: (reg === 'bubble' ? 0.2 : 0.17) * vel, attack: 0.012, release: Math.min(0.09, d * 0.3), lp: 5200, q: 0.5, dest, essential: ess, vib: solo ? { hz: 6.8, cents: 5, delay: 0.1 } : undefined });
  // Percussion: a 2nd-harmonic ping that decays whatever the note length.
  if (reg !== 'pad') tone(mx, { type: 'sine', f0: f * 2, t, dur: 0.22, gain: 0.08 * vel, attack: 0.002, dest, essential: ess });
  // Key click.
  noiseBurst(mx, { kind: 'white', t, dur: 0.008, gain: 0.03 * vel, filter: 'highpass', f0: 2500, dest, essential: ess });
}

/** Legacy name: sustained organ (stingers). */
export function organ(mx: Mixer, dest: AudioNode, t: number, midi: number, dur: number, vel: number, ess = false): void {
  hammond(mx, dest, t, midi, dur, vel * 0.8, ess, 'comp');
}

/** Upright-ish piano: bright band-limited saw with a fast lowpass decay over a sine body. */
export function piano(mx: Mixer, dest: AudioNode, t: number, midi: number, dur: number, vel: number, ess = false): void {
  const f = midiToHz(midi);
  const d = Math.max(0.08, dur);
  tone(mx, { wave: mx.sawLimited, f0: f, t, dur: d + 0.15, gain: 0.13 * vel, attack: 0.002, release: d * 0.6, lp: Math.min(f * 7, 6000), lpTo: Math.max(300, f * 1.5), q: 0.6, dest, essential: ess });
  tone(mx, { type: 'sine', f0: f, t, dur: d + 0.15, gain: 0.1 * vel, attack: 0.002, release: d * 0.6, dest, essential: ess });
  ringTick(mx, dest, t, Math.min(3000, f * 4), 1.5, 0.02 * vel, 0.02, ess);
}

/** Twangy electric guitar: a plucked saw with a snapping lowpass, an octave sine body and pick noise. */
export function guitar(mx: Mixer, dest: AudioNode, t: number, midi: number, dur: number, vel: number, ess = false): void {
  const f = midiToHz(midi);
  const d = Math.max(0.09, dur);
  tone(mx, { wave: mx.sawLimited, f0: f, t, dur: d + 0.1, gain: 0.14 * vel, attack: 0.002, release: d * 0.5, lp: Math.min(f * 9, 5500), lpTo: Math.max(400, f * 2.2), q: 1.4, dest, essential: ess });
  tone(mx, { type: 'sine', f0: f, t, dur: d, gain: 0.07 * vel, attack: 0.002, release: d * 0.5, dest, essential: ess });
  noiseBurst(mx, { kind: 'white', t, dur: 0.006, gain: 0.05 * vel, filter: 'bandpass', f0: 3200, q: 1.2, dest, essential: ess });
}

/** Palm-muted guitar chop ("chk"): short, dark, more pick than string. */
export function guitarMute(mx: Mixer, dest: AudioNode, t: number, midi: number, dur: number, vel: number, ess = false): void {
  const f = midiToHz(midi);
  const d = Math.min(0.09, Math.max(0.045, dur));
  tone(mx, { wave: mx.sawLimited, f0: f, t, dur: d, gain: 0.11 * vel, attack: 0.002, lp: Math.min(f * 5, 2600), lpTo: Math.max(250, f), q: 1, dest, essential: ess });
  noiseBurst(mx, { kind: 'white', t, dur: 0.012, gain: 0.06 * vel, filter: 'bandpass', f0: 2400, q: 1, dest, essential: ess });
}

/** String section: two detuned band-limited saws, slow bow attack, delayed vibrato, warm lowpass. */
export function strings(mx: Mixer, dest: AudioNode, t: number, midi: number, dur: number, vel: number, ess = false): void {
  const f = midiToHz(midi);
  const d = Math.max(0.3, dur);
  const attack = Math.min(0.28, d * 0.35);
  const lp = Math.min(f * 5, 3600);
  tone(mx, { wave: mx.sawLimited, f0: f, t, dur: d, gain: 0.055 * vel, attack, release: Math.min(0.35, d * 0.4), detune: 7, lp, q: 0.5, dest, essential: ess, vib: { hz: 5.4, cents: 9, delay: 0.25 } });
  tone(mx, { wave: mx.sawLimited, f0: f, t, dur: d, gain: 0.05 * vel, attack: attack * 1.15, release: Math.min(0.35, d * 0.4), detune: -8, lp: lp * 0.85, q: 0.5, dest, essential: ess, vib: { hz: 4.9, cents: 8, delay: 0.3 } });
}

/** Flute: a pure tone with a little second harmonic, breath noise and vibrato that arrives late. */
export function flute(mx: Mixer, dest: AudioNode, t: number, midi: number, dur: number, vel: number, ess = false): void {
  const f = midiToHz(midi);
  const d = Math.max(0.1, dur);
  tone(mx, { type: 'sine', f0: f, t, dur: d, gain: 0.2 * vel, attack: 0.045, release: Math.min(0.12, d * 0.35), dest, essential: ess, vib: { hz: 5.2, cents: 14, delay: 0.18 } });
  tone(mx, { type: 'triangle', f0: f, t, dur: d, gain: 0.05 * vel, attack: 0.06, release: Math.min(0.12, d * 0.35), lp: f * 3, dest, essential: ess });
  noiseBurst(mx, { kind: 'pink', t, dur: d, gain: 0.03 * vel, attack: 0.03, release: Math.min(0.1, d * 0.3), filter: 'bandpass', f0: f * 2, q: 6, dest, essential: ess });
}

/** Round reggae / soul bass: triangle + sine sub through a gentle lowpass. */
export function softBass(mx: Mixer, dest: AudioNode, t: number, midi: number, dur: number, vel: number, ess = false): void {
  const f = midiToHz(midi);
  tone(mx, { type: 'triangle', f0: f, t, dur: dur + 0.1, gain: 0.38 * vel, attack: 0.012, release: 0.08, lp: 700, lpTo: 300, dest, essential: ess });
  tone(mx, { type: 'sine', f0: f, t, dur: dur + 0.1, gain: 0.24 * vel, attack: 0.012, release: 0.08, dest, essential: ess });
}

/** Motown fingered Precision: saw + triangle with a quick lowpass pluck and a thumb thump. */
export function fingerBass(mx: Mixer, dest: AudioNode, t: number, midi: number, dur: number, vel: number, ess = false): void {
  const f = midiToHz(midi);
  const d = Math.max(0.09, dur);
  tone(mx, { wave: mx.sawLimited, f0: f, t, dur: d, gain: 0.26 * vel, attack: 0.006, release: d * 0.5, lp: 1300, lpTo: 380, q: 1.5, dest, essential: ess });
  tone(mx, { type: 'triangle', f0: f, t, dur: d, gain: 0.3 * vel, attack: 0.006, release: d * 0.5, lp: 600, dest, essential: ess });
  noiseBurst(mx, { kind: 'brown', t, dur: 0.02, gain: 0.12 * vel, filter: 'lowpass', f0: 500, dest, essential: ess });
}

/** Picked mod bass: bright saw with a fast resonant filter pluck. */
export function pickBass(mx: Mixer, dest: AudioNode, t: number, midi: number, dur: number, vel: number, ess = false): void {
  const f = midiToHz(midi);
  const d = Math.max(0.09, dur);
  tone(mx, { type: 'sawtooth', f0: f, t, dur: d, gain: 0.3 * vel, attack: 0.003, release: d * 0.55, lp: 1900, lpTo: 320, q: 4, dest, essential: ess });
  tone(mx, { type: 'sine', f0: f, t, dur: d, gain: 0.28 * vel, attack: 0.003, release: d * 0.55, dest, essential: ess });
}

export const funkBass = pickBass;

/** Upright (double) bass: woody thump, sine body, quick decay into a soft sustain. */
export function uprightBass(mx: Mixer, dest: AudioNode, t: number, midi: number, dur: number, vel: number, ess = false): void {
  const f = midiToHz(midi);
  const d = Math.max(0.12, dur);
  tone(mx, { type: 'sine', f0: f, t, dur: d, gain: 0.42 * vel, attack: 0.008, release: d * 0.6, dest, essential: ess });
  tone(mx, { type: 'triangle', f0: f, t, dur: Math.min(d, 0.35), gain: 0.14 * vel, attack: 0.004, lp: 900, lpTo: 250, dest, essential: ess });
  noiseBurst(mx, { kind: 'brown', t, dur: 0.03, gain: 0.2 * vel, filter: 'lowpass', f0: 350, dest, essential: ess });
}

/** Wah-guitar chop: saw stack through an envelope-swept resonant bandpass (stingers / legacy). */
export function wahChop(mx: Mixer, dest: AudioNode, t: number, midis: readonly number[], dur: number, vel: number, ess = false): void {
  for (const m of midis) {
    const f = midiToHz(m);
    tone(mx, { type: 'sawtooth', f0: f, t, dur, gain: 0.09 * vel, attack: 0.003, release: dur * 0.45, bp: 450, bpTo: 2100, q: 5, dest, essential: ess });
  }
}

/** Brass section stab / line: detuned band-limited saws with an opening lowpass. */
export function hornStab(mx: Mixer, dest: AudioNode, t: number, midis: readonly number[], dur: number, vel: number, ess = false): void {
  for (const m of midis) {
    const f = midiToHz(m);
    const lp = Math.min(f * 5, 5200);
    tone(mx, { wave: mx.sawLimited, f0: f, t, dur, gain: 0.085 * vel, attack: 0.025, release: dur * 0.4, lp: lp * 0.35, lpTo: lp, q: 1.2, detune: 6, dest, essential: ess });
    tone(mx, { type: 'sawtooth', f0: f, t, dur, gain: 0.05 * vel, attack: 0.03, release: dur * 0.4, lp: lp * 0.3, lpTo: lp * 0.8, detune: -7, dest, essential: ess });
  }
}

/** Sustained brass note (used by stingers). */
export function brass(mx: Mixer, dest: AudioNode, t: number, midi: number, dur: number, vel: number, ess = false): void {
  hornStab(mx, dest, t, [midi], dur, vel * 1.4, ess);
}

/** Vibraphone: a pure bar tone with its 4x partial, soft mallet, long decay. */
export function vibes(mx: Mixer, dest: AudioNode, t: number, midi: number, dur: number, vel: number, ess = false): void {
  const f = midiToHz(midi);
  const d = Math.max(0.5, dur + 0.6);
  tone(mx, { type: 'sine', f0: f, t, dur: d, gain: 0.2 * vel, attack: 0.004, dest, essential: ess });
  tone(mx, { type: 'sine', f0: f * 3.98, t, dur: d * 0.35, gain: 0.05 * vel, attack: 0.002, dest, essential: ess });
  noiseBurst(mx, { kind: 'pink', t, dur: 0.02, gain: 0.03 * vel, filter: 'bandpass', f0: f * 2, q: 2, dest, essential: ess });
}

/** Melodica: two slightly beating reeds (band-limited saws) through a small, nasal resonance. */
export function melodica(mx: Mixer, dest: AudioNode, t: number, midi: number, dur: number, vel: number, ess = false): void {
  const f = midiToHz(midi);
  const d = Math.max(0.08, dur);
  tone(mx, { wave: mx.sawLimited, f0: f, t, dur: d, gain: 0.07 * vel, attack: 0.03, release: Math.min(0.08, d * 0.4), bp: 1300, q: 0.9, dest, essential: ess });
  tone(mx, { wave: mx.sawLimited, f0: f, t, dur: d, gain: 0.05 * vel, attack: 0.035, release: Math.min(0.08, d * 0.4), detune: 9, lp: 2600, q: 0.6, dest, essential: ess });
}

/** Pitched bell / coin-ping. */
export function bell(mx: Mixer, dest: AudioNode, t: number, midi: number, dur: number, vel: number, ess = false): void {
  bellPartials(mx, dest, t, midiToHz(midi), dur, 0.18 * vel, [1, 2.76, 5.4], ess);
}

/** The kit. `dur` only matters for the brush sweep and the open hat / ride / crash (seconds). */
export function drum(mx: Mixer, dest: AudioNode, t: number, kind: DrumKind, vel: number, ess = false, dur = 0): void {
  switch (kind) {
    case 'kick':
      tone(mx, { type: 'sine', f0: 140, f1: 45, sweepTime: 0.1, t, dur: 0.28, gain: 0.7 * vel, attack: 0.002, dest, essential: ess });
      noiseBurst(mx, { kind: 'pink', t, dur: 0.02, gain: 0.12 * vel, filter: 'lowpass', f0: 2500, dest, essential: ess });
      break;
    case 'kickSoft':
      tone(mx, { type: 'sine', f0: 110, f1: 48, sweepTime: 0.09, t, dur: 0.22, gain: 0.5 * vel, attack: 0.003, dest, essential: ess });
      break;
    case 'snare':
      noiseBurst(mx, { kind: 'white', t, dur: 0.16, gain: 0.3 * vel, filter: 'bandpass', f0: 1900, q: 0.8, dest, essential: ess });
      tone(mx, { type: 'triangle', f0: 210, f1: 150, sweepTime: 0.08, t, dur: 0.12, gain: 0.25 * vel, dest, essential: ess });
      break;
    case 'brush':
      // A sweep across the head: slow swell of hissy noise.
      noiseBurst(mx, { kind: 'white', t, dur: Math.max(0.2, dur), gain: 0.16 * vel, attack: Math.max(0.03, dur * 0.4), filter: 'highpass', f0: 2600, dest, essential: ess });
      break;
    case 'hat':
      noiseBurst(mx, { kind: 'white', t, dur: 0.045, gain: 0.11 * vel, filter: 'highpass', f0: 7200, dest, essential: ess });
      break;
    case 'hatOpen':
      noiseBurst(mx, { kind: 'white', t, dur: Math.max(0.2, dur), gain: 0.1 * vel, filter: 'highpass', f0: 6500, dest, essential: ess });
      break;
    case 'rim':
      tone(mx, { type: 'square', f0: 1700, t, dur: 0.03, gain: 0.08 * vel, bp: 1700, q: 3, dest, essential: ess });
      break;
    case 'ride':
      noiseBurst(mx, { kind: 'white', t, dur: Math.max(0.4, dur), gain: 0.07 * vel, filter: 'bandpass', f0: 6200, q: 1.2, dest, essential: ess });
      tone(mx, { type: 'sine', f0: 3480, t, dur: 0.25, gain: 0.03 * vel, attack: 0.001, dest, essential: ess });
      break;
    case 'crash':
      noiseBurst(mx, { kind: 'white', t, dur: Math.max(1.1, dur), gain: 0.16 * vel, attack: 0.004, filter: 'highpass', f0: 3400, dest, essential: ess });
      noiseBurst(mx, { kind: 'white', t, dur: 0.5, gain: 0.08 * vel, filter: 'bandpass', f0: 5200, q: 1.5, dest, essential: ess });
      break;
    case 'tomLo':
      tone(mx, { type: 'sine', f0: 118, f1: 82, sweepTime: 0.2, t, dur: 0.32, gain: 0.5 * vel, attack: 0.002, dest, essential: ess });
      noiseBurst(mx, { kind: 'pink', t, dur: 0.02, gain: 0.08 * vel, filter: 'lowpass', f0: 1800, dest, essential: ess });
      break;
    case 'tomHi':
      tone(mx, { type: 'sine', f0: 180, f1: 130, sweepTime: 0.16, t, dur: 0.26, gain: 0.45 * vel, attack: 0.002, dest, essential: ess });
      noiseBurst(mx, { kind: 'pink', t, dur: 0.02, gain: 0.08 * vel, filter: 'lowpass', f0: 2200, dest, essential: ess });
      break;
    case 'tamb':
      noiseBurst(mx, { kind: 'white', t, dur: 0.07, gain: 0.09 * vel, filter: 'highpass', f0: 6000, dest, essential: ess });
      noiseBurst(mx, { kind: 'white', t: t + 0.02, dur: 0.05, gain: 0.05 * vel, filter: 'highpass', f0: 7500, dest, essential: ess });
      ringTick(mx, dest, t, 8800, 6, 0.04 * vel, 0.04, ess);
      break;
    case 'shaker':
      noiseBurst(mx, { kind: 'white', t, dur: 0.09, gain: 0.07 * vel, attack: 0.018, filter: 'highpass', f0: 5200, dest, essential: ess });
      break;
    case 'conga':
      tone(mx, { type: 'sine', f0: 195, f1: 168, sweepTime: 0.06, t, dur: 0.2, gain: 0.32 * vel, attack: 0.002, dest, essential: ess });
      noiseBurst(mx, { kind: 'white', t, dur: 0.015, gain: 0.06 * vel, filter: 'bandpass', f0: 1100, q: 1.5, dest, essential: ess });
      break;
  }
}
